/*
 * The three things this browser is allowed to remember, and the guards around them.
 *
 * WHY EVERY ACCESS IS WRAPPED, INCLUDING THE READS
 * ------------------------------------------------
 * `window.localStorage` is not a property you can safely touch. Safari in private
 * browsing throws `QuotaExceededError` on `setItem`; a browser with site data
 * blocked throws `SecurityError` on the very first *property access*, before any
 * method is called; an iframe with a null origin does the same. A storefront that
 * reads a bag count inside a `try` but probes `typeof localStorage` outside one
 * still takes the whole bundle down on those browsers, which is why the probe below
 * is itself inside a `try` and why every helper here has its own.
 *
 * The failure mode is always "we forgot", never "the page broke": a browser that
 * cannot store anything gets a storefront with no bag badge and no remembered list,
 * and every page still works exactly as the server rendered it.
 *
 * WHAT MAY NEVER BE KEPT HERE
 * ---------------------------
 * No mobile number, no name, no order number, no token, no privilege card number,
 * and no price the customer is going to be charged. A shared phone at a shop counter
 * is a real device in this shop's day, and localStorage outlives the session that
 * wrote it. The bag keeps ITEM CODES and a *display* total that is re-quoted by the
 * server on the next page load (BUILD_DECISIONS sections 4 and 11) — nothing here is
 * ever an input to what anybody pays.
 *
 * WHY VALUES ARE RE-VALIDATED ON THE WAY OUT
 * ------------------------------------------
 * Anything in localStorage is attacker-writable on a shared machine and stale-by-
 * design after a deploy. `href` in particular ends up in an anchor, so it is checked
 * to be a single-slash same-site path on read — the same rule as
 * `api.auth._safe_redirect` and `bag._safe_path`. An open redirect is an open
 * redirect wherever the string came from.
 */

/** One namespace, so a `localStorage.clear()` in the desk never collides with us. */
const PREFIX = "sb:";

const KEYS = {
	lastList: PREFIX + "last-list",
	bag: PREFIX + "bag",
	theme: PREFIX + "theme"
};

/**
 * How long a remembered set stays interesting.
 *
 * A school book list is a once-a-year purchase, so "the list you last opened" is
 * useful for a season and actively wrong after one: showing a parent last year's
 * Class 6 when the child is in Class 7 is worse than showing them the finder.
 */
const LAST_LIST_TTL_MS = 180 * 24 * 60 * 60 * 1000;

/** A stored blob is a convenience, never a document. Anything larger is a bug. */
const MAX_VALUE_LENGTH = 4000;

/** Cached result of the probe below — `null` until the first access. */
let usable = null;

/**
 * Can this browser store anything at all?
 *
 * Probed with a real write-and-delete rather than by feature-detecting the object,
 * because the browsers that matter here (private-mode Safari, site data blocked)
 * expose a perfectly normal-looking `localStorage` that throws only when used.
 */
function available() {
	if (usable !== null) return usable;
	usable = false;
	try {
		const probe = PREFIX + "probe";
		window.localStorage.setItem(probe, "1");
		window.localStorage.removeItem(probe);
		usable = true;
	} catch (error) {
		usable = false;
	}
	return usable;
}

/** Read and parse one key, or `null`. Never throws, never returns a non-object. */
function read(key) {
	if (!available()) return null;
	let raw = null;
	try {
		raw = window.localStorage.getItem(key);
	} catch (error) {
		return null;
	}
	if (!raw || raw.length > MAX_VALUE_LENGTH) return null;
	try {
		const value = JSON.parse(raw);
		return value && typeof value === "object" ? value : null;
	} catch (error) {
		// Written by an older build, or by something else entirely. Drop it rather
		// than leaving a value that will fail to parse on every page load forever.
		drop(key);
		return null;
	}
}

/** Write one key. Returns whether it stuck, which callers may ignore. */
function write(key, value) {
	if (!available()) return false;
	try {
		const raw = JSON.stringify(value);
		if (!raw || raw.length > MAX_VALUE_LENGTH) return false;
		window.localStorage.setItem(key, raw);
		return true;
	} catch (error) {
		// A full quota is not an error a customer can act on, and the page they are
		// looking at is complete without us.
		return false;
	}
}

/** Forget one key. */
function drop(key) {
	if (!available()) return;
	try {
		window.localStorage.removeItem(key);
	} catch (error) {
		/* nothing to do and nothing worth saying */
	}
}

/**
 * A same-site path, or "".
 *
 * The same rule as `splashbox.api.auth._safe_redirect` and `www/bag.py:_safe_path`:
 * one leading separator and no second one, because a browser normalises
 * "/\\evil.example" to "//evil.example" and navigates off site. These strings end up
 * in `href` attributes, and this value came out of storage, which on a shared
 * machine is somebody else's input.
 */
function safePath(raw) {
	const path = typeof raw === "string" ? raw.trim() : "";
	if (!path || path.charAt(0) !== "/") return "";
	const second = path.charAt(1);
	if (second === "/" || second === "\\") return "";
	return path.slice(0, 512);
}

/**
 * A short, single-line display string.
 *
 * Control characters are stripped for the same reason `api._envelope.clean_text`
 * strips them on the server: they turn one line of anything into two. These values
 * are written back into the page with `textContent`, so this is tidiness rather than
 * safety — but a string that came out of storage on a shared machine gets the same
 * treatment as one that came off the wire.
 */
function safeText(raw, max) {
	if (typeof raw !== "string") return "";
	return raw
		.replace(/[\u0000-\u001f\u007f]+/g, " ")
		.replace(/\s{2,}/g, " ")
		.trim()
		.slice(0, max || 140);
}

/** A whole, non-negative count, capped at what a bag could plausibly hold. */
function safeCount(raw) {
	const value = parseInt(raw, 10);
	if (!isFinite(value) || value < 0) return 0;
	return Math.min(value, 9999);
}

/** A whole, non-negative rupee figure. Display only — nobody is charged from here. */
function safeAmount(raw) {
	const value = Math.floor(Number(raw));
	if (!isFinite(value) || value < 0) return 0;
	return Math.min(value, 99999999);
}

/* ── the last list a parent opened ─────────────────────────────────────────── */

/**
 * The set page remembers itself here so the home page can offer it back.
 *
 * The server cannot do this: `/` is rendered for a Guest with no session of their
 * own (frappe gives every signed-out visitor the same "Guest" session —
 * frappe/sessions.py:391 — which is exactly why the bag travels in the URL too), so
 * "the list you were looking at" is knowledge only this browser has.
 */
export const lastList = {
	get: function () {
		const value = read(KEYS.lastList);
		if (!value) return null;
		const href = safePath(value.href);
		if (!href) return null;
		if (value.at && Date.now() - Number(value.at) > LAST_LIST_TTL_MS) {
			drop(KEYS.lastList);
			return null;
		}
		return {
			href: href,
			title: safeText(value.title, 120),
			code: safeText(value.code, 60),
			meta: safeText(value.meta, 120)
		};
	},

	set: function (entry) {
		const href = safePath(entry && entry.href);
		if (!href) return false;
		return write(KEYS.lastList, {
			href: href,
			title: safeText(entry.title, 120),
			code: safeText(entry.code, 60),
			meta: safeText(entry.meta, 120),
			at: Date.now()
		});
	},

	clear: function () {
		drop(KEYS.lastList);
	}
};

/* ── the bag ───────────────────────────────────────────────────────────────── */

/**
 * What the browser is carrying, so the header badge and the Bag links agree with it
 * on every page.
 *
 * `href` is the whole bag as a query string (`/bag?bundle=…&sets=…&removed=…`),
 * which is the only place a bag can live on this storefront — see the long
 * explanation at the top of `www/bag.py`. `count` and `total` are the SERVER's last
 * word on the same bag and are shown, never used: the bag page re-quotes from the
 * URL and `create_order` re-quotes again before it books anything, so a tampered
 * total here changes a badge and nothing else.
 */
export const bag = {
	get: function () {
		const value = read(KEYS.bag);
		if (!value) return null;
		const href = safePath(value.href);
		if (!href) return null;
		return {
			href: href,
			count: safeCount(value.count),
			total: safeAmount(value.total),
			loose: Array.isArray(value.loose) ? value.loose.slice(0, 50) : []
		};
	},

	set: function (entry) {
		const href = safePath(entry && entry.href);
		if (!href) return false;
		const loose = [];
		(Array.isArray(entry.loose) ? entry.loose : []).slice(0, 50).forEach(function (row) {
			const code = safeText(row && row.item_code, 140);
			if (code) loose.push({ item_code: code, qty: Math.max(1, safeCount(row.qty) || 1) });
		});
		return write(KEYS.bag, {
			href: href,
			count: safeCount(entry.count),
			total: safeAmount(entry.total),
			loose: loose,
			at: Date.now()
		});
	},

	clear: function () {
		drop(KEYS.bag);
	}
};

/* ── the theme ─────────────────────────────────────────────────────────────── */

/**
 * "light", "dark" or "system".
 *
 * `system` is stored as the ABSENCE of the key, so a customer who never expressed a
 * preference keeps following their device — and so does a customer who switches back
 * to it. The palette in `public/scss/_tokens.scss` is built the same way round:
 * `:root[data-theme="dark"]` and `:root[data-theme="light"]` are the explicit
 * choices, and with no attribute at all only `prefers-color-scheme` decides.
 */
export const theme = {
	get: function () {
		const value = read(KEYS.theme);
		const name = value && typeof value.name === "string" ? value.name : "";
		return name === "dark" || name === "light" ? name : "system";
	},

	set: function (name) {
		if (name !== "dark" && name !== "light") {
			drop(KEYS.theme);
			return true;
		}
		return write(KEYS.theme, { name: name });
	}
};

/** Exported for the modules that want to know whether remembering is possible. */
export { available };
